import React, { JSX, Key, useMemo } from 'react';
import { FilterOutlined } from '@ant-design/icons';
import { GridMenuItem } from '../grid-menu-item';
import { MouseActions, TableRenderComponents } from '../../models';
import { DateRangeValue, Identifiable } from '@custis/common';
import style from './GridFilterMenu.module.less';
import { Popover } from 'antd';
import { ColumnInstance } from 'react-table';
import { GridStore } from '../../GridStore';
import { observer } from 'mobx-react';
import { isNil } from 'lodash';
import { GridFilterProps } from '../../filters';
import { presetGpnDefault, Theme } from '@consta/uikit/Theme';

interface GridFilterMenuProps<TItem extends Identifiable, TFilter, TSorting, TResultAdditionalInfo = undefined> {
  columns: ColumnInstance<TItem>[];
  store: GridStore<TItem, TFilter, TSorting, TResultAdditionalInfo>;
  additionalFilters?: AdditionalFilter[];
}

interface AdditionalFilter {
  title: string;
  filterName: string;
  renderFunction: (props: GridFilterProps) => JSX.Element;
}

const GridFilterMenu = <T extends Identifiable, TFilter, TSort, TResultAdditionalInfo = undefined>(
  props: GridFilterMenuProps<T, TFilter, TSort, TResultAdditionalInfo>,
): JSX.Element => {
  return (
    <GridMenuItem>
      <Popover
        trigger={MouseActions.Click}
        content={<GridFilterMenuContent {...props} />}
        placement={'bottomRight'}
        overlayStyle={{ width: 650 }}
        overlayClassName={style.Modal}
      >
        <FilterOutlined />
      </Popover>
    </GridMenuItem>
  );
};

const GridFilterMenuContent = <T extends Identifiable, TFilter, TSort, TResultAdditionalInfo = undefined>({
  columns,
  store,
  additionalFilters = [],
}: GridFilterMenuProps<T, TFilter, TSort, TResultAdditionalInfo>): JSX.Element => {
  return (
    <Theme preset={presetGpnDefault}>
      <div className={style.MenuContent}>
        {columns.map((column) => (
          <GridFilterItem key={column.id} column={column} />
        ))}
        {additionalFilters.map((additionalFilter) => (
          <AdditionalFilterItem key={additionalFilter.filterName} filter={additionalFilter} store={store} />
        ))}
      </div>
    </Theme>
  );
};

const GridFilterItem = <TItem extends Identifiable>({ column }: { column: ColumnInstance<TItem> }) => {
  const properties = useMemo(
    () => ({
      value: column.filterValue as unknown,
      orientation: 'row',
      buttonStyle: 'mini',
      filterInputClassName: style.GridFilterWrapper,
      onConfirm: (value: Key[] | DateRangeValue | undefined) => {
        column.setFilter(Array.isArray(value) ? (value?.length === 0 ? undefined : value) : value);
      },
      onReset: () => {
        column.setFilter(undefined);
      },
    }),
    [column],
  );

  return column.Filter ? (
    <div>
      <div>{column.render(TableRenderComponents.Header)}</div>
      <div>{column.render(TableRenderComponents.Filter, properties)}</div>
    </div>
  ) : null;
};

const AdditionalFilterItem = observer(
  <TItem extends Identifiable, TFilter, TSorting, TResultAdditionalInfo = undefined>({
    store,
    filter: { filterName, title, renderFunction },
  }: {
    store: GridStore<TItem, TFilter, TSorting, TResultAdditionalInfo>;
    filter: AdditionalFilter;
  }) => {
    const filter = store.urlState?.value?.filter as Record<string, unknown>;

    const properties = useMemo(
      () =>
        ({
          value: !isNil(filter) ? filter[filterName] : undefined,
          orientation: 'row',
          buttonStyle: 'mini',
          onReset: () => {
            store.updateFilters({ [filterName]: undefined });
          },
          onConfirm: (value: unknown) => {
            store.updateFilters({ [filterName]: value });
          },
        }) as GridFilterProps,
      [filter],
    );

    return (
      <div key={filterName}>
        <div>{title}</div>
        <div>{renderFunction(properties)}</div>
      </div>
    );
  },
);

export { GridFilterMenu, AdditionalFilter };
